import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Statistics.css";

const MIDDLEWARE_URL = process.env.REACT_APP_API_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(Number(n) || 0));

function Statistics() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statsFull, setStatsFull] = useState(null);
  const [loading, setLoading] = useState(true);

  const [societes, setSocietes] = useState([]);
  const [selectedSociete, setSelectedSociete] = useState("all");
  const [selectedAnnee, setSelectedAnnee] = useState(String(new Date().getFullYear()));
  const [caMensuel, setCaMensuel] = useState([]);
  const [repartition, setRepartition] = useState([]);
  const [caLoading, setCaLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${MIDDLEWARE_URL}/archive/statistics`),
          fetch(`${MIDDLEWARE_URL}/archive/statistics/full`),
        ]);
        const d1 = await r1.json();
        const d2 = await r2.json();
        setStats(d1);
        setStatsFull(d2);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchAll();
  }, []);

  useEffect(() => {
    const fetchSocietes = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/dashboard/societes`);
        const data = await r.json();
        setSocietes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("[Sociétés]", err);
      }
    };
    fetchSocietes();
  }, []);

  useEffect(() => {
    const fetchCA = async () => {
      setCaLoading(true);
      try {
        const socParam = selectedSociete === "all" ? "" : `&societe_id=${selectedSociete}`;
        const [r1, r2] = await Promise.all([
          fetch(`${BACKEND_URL}/api/dashboard/ca-mensuel?annee=${selectedAnnee}${socParam}`),
          fetch(`${BACKEND_URL}/api/dashboard/repartition-clients?annee=${selectedAnnee}`),
        ]);
        const d1 = await r1.json();
        const d2 = await r2.json();
        setCaMensuel(Array.isArray(d1) ? d1 : []);
        setRepartition(Array.isArray(d2) ? d2 : []);
      } catch (err) {
        console.error("[CA]", err);
      }
      setCaLoading(false);
    };
    fetchCA();
  }, [selectedAnnee, selectedSociete]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const s = stats?.statistics || {};
  const sf = statsFull?.statistics || {};

  const monthlyValues = MONTHS.map((_, i) => {
    const found = caMensuel.find((row) => Number(row.mois) === i + 1);
    return found ? Number(found.total) : 0;
  });
  const maxVal = Math.max(...monthlyValues, 1);
  const totalCA = monthlyValues.reduce((a, b) => a + b, 0);
  const repartitionTotal = repartition.reduce((a, r) => a + Number(r.total), 0) || 1;

  return (
    <div className="stats-page">
      <nav className="stats-nav">
        <div className="nav-logo" onClick={() => navigate("/home")}>⚡ LOGIMES</div>
        <div className="nav-links">
          <span onClick={() => navigate("/home")}>🏠 Accueil</span>
          <span onClick={() => navigate("/factures")}>🧾 Factures</span>
          <span className="active">📊 Statistiques</span>
        </div>
        <button className="nav-logout" onClick={handleLogout}>Déconnexion</button>
      </nav>

      <div className="stats-header">
        <h1>📊 Statistiques</h1>
        <p>Vue d'ensemble de l'activité de facturation électronique</p>
        {stats && (
          <div className="env-badge">
            🌐 Environnement : <strong>{stats.environment || "—"}</strong>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            🧪 Mode Simulation : <strong>{stats.simulation_mode ? "Oui" : "Non"}</strong>
            {statsFull?.legal_retention_years && (
              <>&nbsp;&nbsp;|&nbsp;&nbsp;📅 Rétention légale : <strong>{statsFull.legal_retention_years} ans</strong></>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="stats-loading">⏳ Chargement des statistiques...</div>
      ) : (
        <div className="stats-content">

          <section className="stats-section">
            <h2>📁 Résumé Archive</h2>
            <div className="kpi-grid">
              <div className="kpi-card kpi-purple">
                <div className="kpi-icon">🧾</div>
                <div className="kpi-value">{s.total ?? "—"}</div>
                <div className="kpi-label">Total Factures</div>
              </div>
              <div className="kpi-card kpi-blue">
                <div className="kpi-icon">📅</div>
                <div className="kpi-value">{s.today ?? "—"}</div>
                <div className="kpi-label">Aujourd'hui</div>
              </div>
              <div className="kpi-card kpi-green">
                <div className="kpi-icon">📆</div>
                <div className="kpi-value">{s.last_30_days ?? "—"}</div>
                <div className="kpi-label">30 Derniers Jours</div>
              </div>
            </div>
          </section>

          {s.by_status && Object.keys(s.by_status).length > 0 && (
            <section className="stats-section">
              <h2>📌 Par Statut</h2>
              <div className="kpi-grid">
                {Object.entries(s.by_status).map(([status, count], i) => (
                  <div className="kpi-card kpi-orange" key={i}>
                    <div className="kpi-icon">🏷️</div>
                    <div className="kpi-value">{count}</div>
                    <div className="kpi-label">{status}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {s.by_environment && Object.keys(s.by_environment).length > 0 && (
            <section className="stats-section">
              <h2>🌐 Par Environnement</h2>
              <div className="kpi-grid">
                {Object.entries(s.by_environment).map(([env, count], i) => (
                  <div className="kpi-card kpi-teal" key={i}>
                    <div className="kpi-icon">🖥️</div>
                    <div className="kpi-value">{count}</div>
                    <div className="kpi-label">{env}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {statsFull && JSON.stringify(sf) !== JSON.stringify(s) && (
            <section className="stats-section">
              <h2>📈 Statistiques Complètes</h2>
              <div className="kpi-grid">
                <div className="kpi-card kpi-purple">
                  <div className="kpi-icon">🧾</div>
                  <div className="kpi-value">{sf.total ?? "—"}</div>
                  <div className="kpi-label">Total (Complet)</div>
                </div>
                <div className="kpi-card kpi-blue">
                  <div className="kpi-icon">📅</div>
                  <div className="kpi-value">{sf.today ?? "—"}</div>
                  <div className="kpi-label">Aujourd'hui (Complet)</div>
                </div>
                <div className="kpi-card kpi-green">
                  <div className="kpi-icon">📆</div>
                  <div className="kpi-value">{sf.last_30_days ?? "—"}</div>
                  <div className="kpi-label">30 Jours (Complet)</div>
                </div>
              </div>
            </section>
          )}

          <section className="stats-section">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
              <h2 style={{ margin: 0 }}>💰 Chiffre d'Affaires</h2>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <select
                  value={selectedAnnee}
                  onChange={(e) => setSelectedAnnee(e.target.value)}
                  style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #ddd" }}
                >
                  {[2024, 2025, 2026].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <select
                  value={selectedSociete}
                  onChange={(e) => setSelectedSociete(e.target.value)}
                  style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #ddd", minWidth: "180px" }}
                >
                  <option value="all">Toutes les sociétés</option>
                  {societes.map((soc) => (
                    <option key={soc.id} value={soc.id}>{soc.nom}</option>
                  ))}
                </select>
              </div>
            </div>

            {caLoading ? (
              <div className="stats-loading">⏳ Chargement du CA...</div>
            ) : (
              <>
                <div className="kpi-grid" style={{ marginBottom: "20px" }}>
                  <div className="kpi-card kpi-purple">
                    <div className="kpi-icon">💵</div>
                    <div className="kpi-value">{fmt(totalCA)} TND</div>
                    <div className="kpi-label">CA Total {selectedAnnee}</div>
                  </div>
                  <div className="kpi-card kpi-blue">
                    <div className="kpi-icon">🏢</div>
                    <div className="kpi-value">{repartition.length}</div>
                    <div className="kpi-label">Clients Actifs</div>
                  </div>
                </div>

                <div style={{ background: "#fff", borderRadius: "12px", padding: "20px", marginBottom: "20px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                  <p style={{ margin: "0 0 16px", fontWeight: 600, color: "#444" }}>CA mensuel — {selectedAnnee}</p>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", height: "200px" }}>
                    {monthlyValues.map((val, i) => (
                      <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                        <div
                          title={`${fmt(val)} TND`}
                          style={{
                            width: "100%",
                            height: `${Math.max((val / maxVal) * 160, val > 0 ? 4 : 0)}px`,
                            background: val > 0 ? "#6c63ff" : "#eee",
                            borderRadius: "4px 4px 0 0",
                            transition: "height 0.3s",
                          }}
                        />
                        <span style={{ fontSize: "11px", color: "#888" }}>{MONTHS[i]}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ background: "#fff", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                  <p style={{ margin: "0 0 16px", fontWeight: 600, color: "#444" }}>Répartition par client</p>
                  {repartition.length === 0 ? (
                    <p style={{ color: "#999", fontSize: "14px" }}>Aucune donnée pour cette période.</p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {repartition.map((r, i) => {
                        const pct = Math.round((Number(r.total) / repartitionTotal) * 100);
                        return (
                          <div key={i}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
                              <span>{r.client || "—"}</span>
                              <span style={{ color: "#888" }}>{fmt(r.total)} TND · {pct}%</span>
                            </div>
                            <div style={{ height: "8px", background: "#f0f0f0", borderRadius: "4px", overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${pct}%`, background: "#6c63ff", borderRadius: "4px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </section>

        </div>
      )}
    </div>
  );
}

export default Statistics;